// OrganizationsTable.js

import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Badge } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { People } from '@mui/icons-material';
import styled from '@emotion/styled';
import B2BTable from '../../../b2b-common/B2BTable';
import { fetchOrganizationDataByUUID } from '../../../../services/b2b-users-apiService';
import { LoginContext } from '../../../../App';

import './OrganizationsTable.css'; // Import the CSS file

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -8,
        top: 10,
        border: `1px solid ${theme.palette.background.paper}`,
        padding: '0 1px',
    },
}));

const OrganizationsTable = ({ organizations, onSelectOrganization, pagesCount, currentPage, onPageChange, className }) => {
    // Filter out organizations with the type 'SYS_MASTER'
    const filteredOrganizations = organizations.content.filter(org => org.organizationDTO.organizationBack2BluType !== 'SYS_MASTER');

    const { loginData } = useContext(LoginContext);
    const [error, setError] = React.useState(null);
    const [selectedOrganization, setSelectedOrganization] = React.useState(null);
    const [rowLoading, setRowLoading] = React.useState(null);

    // Fetch organization data by UUID

    const fetchOrganizationData = async (organizationUUID) => {
        try {
            if (loginData) {
                setRowLoading(organizationUUID);
                const { accessToken } = loginData;
                const data = await fetchOrganizationDataByUUID(organizationUUID, accessToken);
                setSelectedOrganization(data);
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setRowLoading(null);
        }
    }

    useEffect(() => {
        if (selectedOrganization) {
            if (onSelectOrganization && selectedOrganization) {
                onSelectOrganization(selectedOrganization);
            }
        }
    }, [selectedOrganization])

    const handleRowSelected = (rowSelected) => {
        fetchOrganizationData(rowSelected.id);
    };

    const headers = [
        { label: 'Organization Name', align: 'left' },
        { label: 'Members', align: 'center' },
        { label: 'Licensed', align: 'center' },
        { label: 'Enabled', align: 'center' },
    ];

    const rows = filteredOrganizations.map((org) => ({
        id: org.organizationDTO.organizationUUID,
        cells: [
            { content: `${org.organizationDTO.organizationName}`, align: 'left' },
            {
                content: (
                    <StyledBadge badgeContent={org.membersCount} color="secondary" showZero className="badge">
                        <People color="action" className="icon" />
                    </StyledBadge>
                ), align: 'center'
            },
            {
                content: (
                    org.hasValidLicense ? (
                        <CheckBoxIcon className="icon checked" />
                    ) : (
                        <CheckBoxOutlineBlankIcon className="icon" />
                    )
                ), align: 'center'
            },
            {
                content: (
                    org.organizationDTO.organizationEnabled ? (
                        <CheckBoxIcon className="icon checked" />
                    ) : (
                        <CheckBoxOutlineBlankIcon className="icon" />
                    )
                ), align: 'center'
            },
        ]
    }));

    //console.log(organizations);

    return <B2BTable
        pagesCount={pagesCount}
        currentPage={currentPage}
        onPageChange={onPageChange}
        headers={headers} rows={rows}
        rowLoading={rowLoading}
        onRowClick={handleRowSelected}
        className={className} />;
};

OrganizationsTable.propTypes = {
    organizations: PropTypes.shape({
        content: PropTypes.arrayOf(PropTypes.shape({
            organizationDTO: PropTypes.shape({
                organizationId: PropTypes.number.isRequired,
                organizationName: PropTypes.string.isRequired,
                organizationMembers: PropTypes.array.isRequired,
                organizationEnabled: PropTypes.bool.isRequired,
                parentOrg: PropTypes.any,
                imagePath: PropTypes.string,
                activeLicense: PropTypes.any
            }).isRequired,
            membersCount: PropTypes.number.isRequired,
            licenseCount: PropTypes.number.isRequired,
            hasValidLicense: PropTypes.bool.isRequired,
        })).isRequired,
    }),
    onSelectOrganization: PropTypes.func,
    className: PropTypes.string,
};

OrganizationsTable.defaultProps = {
    onSelectOrganization: null,
    className: '',
};

export default OrganizationsTable;